/**
 * MCP Endpoints Tab — Shows all apps with MCP endpoint status.
 * Part of the API Expose Dashboard.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { getMcpEndpoints, toggleAppMcp, rotateAppMcpKey } from '../apps/api';
import { displayHosting } from '../apps/leftoverDevHosting';
import { useAuthStore } from '@/store/authStore';
import { hasMcpLastCallStamp, presentMcpCallCount, presentMcpLastCallLabel } from './mcpEndpointCallHonesty';

interface McpEndpoint {
  app_id: string;
  name: string;
  app_status: string;
  mcp_provisioned: boolean;
  mcp_enabled: boolean;
  mcp_endpoint: string;
  hosting_url: string | null;
  hosting_live?: boolean;
  hosting_honesty?: string | null;
  hosting_honesty_label?: string | null;
  hosting_leftover_url?: string | null;
  custom_domain: string | null;
  total_mcp_calls: number | null;
  last_mcp_call: string | null;
  last_mcp_tool: string | null;
  mcp_calls_counted?: boolean;
}

export default function McpEndpointsTab({ compact = false }: { compact?: boolean }) {
  const [endpoints, setEndpoints] = useState<McpEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const selectedTenant = useAuthStore((s) => s.selectedTenant);

  async function loadEndpoints() {
    setLoading(true);
    setError(null);
    try {
      const data = await getMcpEndpoints();
      setEndpoints(data.endpoints || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadEndpoints(); }, []);

  async function handleToggle(appId: string, currentEnabled: boolean) {
    try {
      await toggleAppMcp(appId, !currentEnabled);
      await loadEndpoints();
    } catch (err: any) {
      setError(err.message);
    }
  }

  function copyText(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: compact ? 2 : 6 }}><CircularProgress size={compact ? 18 : 32} /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ m: compact ? 0 : 2 }}>{error}</Alert>;
  }

  const visibleEndpoints = compact ? endpoints.slice(0, 6) : endpoints;

  return (
    <Box sx={{ p: compact ? 0 : 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: compact ? 1 : 2 }}>
        {!compact && (
          <Typography variant="body2" color="text.secondary">
            MCP endpoints allow Cursor IDE to connect to your app's agent ecosystem via the Model Context Protocol.
          </Typography>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadEndpoints}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {copied && (
        <Alert severity="success" sx={{ mb: 1, py: 0 }}>Copied {copied} to clipboard</Alert>
      )}

      <TableContainer sx={compact ? { maxHeight: 360 } : undefined}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>App</strong></TableCell>
              <TableCell><strong>MCP Endpoint</strong></TableCell>
              <TableCell align="center"><strong>Enabled</strong></TableCell>
              <TableCell align="center"><strong>Status</strong></TableCell>
              {!compact && <TableCell align="right"><strong>Calls</strong></TableCell>}
              {!compact && <TableCell><strong>Last Call</strong></TableCell>}
              {!compact && <TableCell><strong>Hosting</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleEndpoints.length === 0 && (
              <TableRow>
                <TableCell colSpan={compact ? 4 : 7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No apps found. Create an App Instance to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {visibleEndpoints.map((ep) => {
              const lastCallLabel = presentMcpLastCallLabel(ep);
              return (
              <TableRow key={ep.app_id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{ep.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{ep.app_id}</Typography>
                </TableCell>
                <TableCell>
                  {ep.mcp_provisioned ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ep.mcp_endpoint}
                      </Typography>
                      <Tooltip title="Copy URL">
                        <IconButton size="small" onClick={() => copyText(ep.mcp_endpoint, 'endpoint')}>
                          <CopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Chip label="Not provisioned" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="center">
                  {ep.mcp_provisioned && (
                    <Switch
                      size="small"
                      checked={ep.mcp_enabled}
                      onChange={() => handleToggle(ep.app_id, ep.mcp_enabled)}
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={ep.mcp_provisioned ? (ep.mcp_enabled ? 'Active' : 'Disabled') : 'N/A'}
                    size="small"
                    color={ep.mcp_enabled && ep.mcp_provisioned ? 'success' : ep.mcp_provisioned ? 'error' : 'default'}
                    variant={ep.mcp_provisioned ? 'filled' : 'outlined'}
                  />
                </TableCell>
                {!compact && (
                  <TableCell align="right">
                    <Typography variant="body2">{presentMcpCallCount(ep)}</Typography>
                  </TableCell>
                )}
                {!compact && (
                  <TableCell>
                    {hasMcpLastCallStamp(ep) ? (
                      <Box>
                        <Typography variant="caption">{lastCallLabel}</Typography>
                        {ep.last_mcp_tool && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {ep.last_mcp_tool}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">{lastCallLabel}</Typography>
                    )}
                  </TableCell>
                )}
                {!compact && (
                  <TableCell>
                    {(() => {
                      const hosting = displayHosting({
                        tenantId: selectedTenant,
                        hosting_url: ep.hosting_url,
                        custom_domain: ep.custom_domain,
                        hosting_honesty: ep.hosting_honesty,
                        hosting_honesty_label: ep.hosting_honesty_label,
                        hosting_leftover_url: ep.hosting_leftover_url,
                      });
                      if (hosting.leftover) {
                        return (
                          <Typography variant="caption" color="warning.main" title={ep.hosting_leftover_url || ep.hosting_url || undefined}>
                            leftover-dev / not reachable ({hosting.label})
                          </Typography>
                        );
                      }
                      if (hosting.liveUrl) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ep.custom_domain || hosting.liveUrl}
                            </Typography>
                            <Tooltip title="Open">
                              <IconButton size="small" href={hosting.liveUrl} target="_blank">
                                <OpenIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      }
                      return <Typography variant="caption" color="text.secondary">Not deployed</Typography>;
                    })()}
                  </TableCell>
                )}
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
